// @wc-ignore-file
import { type UseMutationResult, useQuery } from "@tanstack/react-query";
import {
  type JiraMappingsAnswer,
  type JiraSendInput,
  type JiraSendOutcome,
  fetchJiraMappings,
} from "./jira-context-api";

/** Every Jira query of a project (the integrations editor invalidates it on save). */
export const jiraProjectKey = (projectId: string) => ["jira", projectId] as const;

/**
 * A send bound to one CR: the owner of the mutation (the CR section) decides
 * what it invalidates, so this feature never imports change-requests.
 */
export type JiraSendMutation = UseMutationResult<
  JiraSendOutcome,
  Error,
  Omit<JiraSendInput, "containerId" | "crId">
>;

/** CR-229: the project's enabled Jira mappings (what the send dialog may offer). */
const jiraMappingsQuery = (projectId: string) => ({
  queryKey: [...jiraProjectKey(projectId), "mappings"],
  queryFn: () => fetchJiraMappings(projectId),
  staleTime: 60 * 1000,
  retry: false,
});

export function useJiraMappings(projectId: string) {
  return useQuery({
    ...jiraMappingsQuery(projectId),
    select: (answer: JiraMappingsAnswer) => answer.mappings,
  });
}

/**
 * CR-268: false once Jira is disconnected (the mappings and links stay). Same
 * request as useJiraMappings; undefined while pending or failed.
 */
export function useJiraConnected(projectId: string) {
  return useQuery({
    ...jiraMappingsQuery(projectId),
    select: (answer: JiraMappingsAnswer) => answer.connected,
  });
}
